import { Box, Button, Card, Grid, styled, Tooltip, useMediaQuery } from "@mui/material";

import useAuthorization from "../../contexts/useAuthorization";
import { useProfileDataCache } from "../../contexts/ProfileDataCache";
import Profile from "./index";
import { LandToken } from "../../types/LandToken";
import { TableSchema } from "../../components/TableView";
import { useEffect, useState } from "react";
import { useAxios } from "../../services/http.service";
import ApiRoutes from "../../config/api.routes";
import { EcoTokenStatus, TERM_UNIT } from "../../utils/constants";
import { useNavigate } from "react-router";
import { AppRoutes, landDetailLink } from "../../config";
import { generatePath, Link } from "react-router-dom";
import {
  baseLandsSchema,
  guardianStatusCol,
  imgCol,
  nameCol,
  ownerCol,
  scoreCol,
  treeMintPoolCol,
} from "../../utils/table-schemas";
import { Row } from "../../components/FlexComponents";
import { EnergySavingsLeafOutlined } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import TableComponent from "../../components/TableView/TableComponent";
import { BlogStoryBlock } from "../../components";
import blogs from "../About/blogs";
import PalmTree from "../../assets/palm.jpg";
import TreeClaimModalNew from "./TreeClaimModalNew";
import moment from "moment";


const ProfileGuarding = () => {
  const navigator = useNavigate();
  const axios = useAxios();
  const { user, isGuardian, isPioneer } = useAuthorization();
  const { getCachedData } = useProfileDataCache();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(560));

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedData, setSelectedData] = useState<any | null>(null);

  const actionsCol: TableSchema<LandToken> = {
    id: "actions",
    label: "Actions",
    style: {
      width: "100px",
    },
    render: (data: LandToken, _index) => {
      const isActive = data.etNft?.easementActivated?.active;
      let path = "";
      let linkName = "";
      if (
        data.status === EcoTokenStatus.VERIFICATION_REQUIRED ||
        data.status === EcoTokenStatus.CLAIMED ||
        data.status === EcoTokenStatus.RE_VERIFICATION_REQUIRED
      ) {
        path = AppRoutes.LandDetails.path;
        linkName = "Details";
      } else if (data.status !== EcoTokenStatus.VERIFICATION_FAILED && (!data.etNft || data.etNft.status === EcoTokenStatus.VERIFIED)) {
        path = AppRoutes.AuctionCreation.path;
        if (!data.etNft) {
          linkName = "Mint";
        } else {
          linkName = "Auction";
        }
      } else {
        path = AppRoutes.LandDetails.path;
        linkName = "Details";
      }

      const _path =
        path === AppRoutes.LandDetails.path ? landDetailLink(data.id) : generatePath(path, { tokenId: data.id });

      return (
        <Row sx={{ gap: 3, alignItems: "center" }}>
          {/* `display: flex` makes the anchor hug the button; without `textDecoration` the
              button, now a block-level flex item rather than an inline-block, inherits the
              anchor's underline. */}
          <Link
            to={_path}
            state={{ ref: AppRoutes.Profile.subRoutes.Guarding.path, mode: "mint" }}
            style={{ display: "flex", textDecoration: "none" }}
          >
            <TableActionButton size="small" color="primary" variant="contained" sx={{ textTransform: "none" }}>
              {linkName}
            </TableActionButton>
          </Link>
          {isActive && (
            <Tooltip title="EFT claiming info">
              <TableActionButton
                disabled={!!selectedId}
                size="small"
                variant="contained"
                sx={tableActionIconSx}
                onClick={() => {
                  setSelectedId(data.etNft?.id as number);
                  setSelectedData({
                    name: data.meta?.name,
                    content: data.content,
                    meta: data.meta,
                    etNft: data.etNft,
                    ecoScore: data.ecoScore,
                    targetDate: moment(data?.etNft?.easementActivated?.contractStart).add(data.meta?.contractPeriod, TERM_UNIT).toDate(),
                  });
                }}
              >
                <EnergySavingsLeafOutlined />
              </TableActionButton>
            </Tooltip>
          )}
        </Row>
      );
    },
  };

  const colSchema: TableSchema<LandToken>[] = [
    ...baseLandsSchema,
    ownerCol,
    guardianStatusCol,
    scoreCol,
    treeMintPoolCol,
    actionsCol,
  ];
  const responsiveSchema: TableSchema<LandToken>[] = [imgCol, nameCol, guardianStatusCol, treeMintPoolCol, actionsCol];

  const [isLoading, setLoading] = useState(true);
  const [tokensLoading, setTokensLoading] = useState(true);
  const [tokens, setTokens] = useState<number>(0);

  useEffect(() => {
    fetchTokens();
  }, []);

  const fetchTokens = async () => {
    setTokensLoading(true);
    try {
      const response = await axios.get(ApiRoutes.user.profile.tokens.root + "?total=true").then((res) => res.data);
      setTokens(response);
      if (!response) {
        setLoading(false);
      }
    } catch (e: any) {
      console.log({ e });
    } finally {
      setTokensLoading(false);
    }
  };

  const emailVerified = user?.emailVerified;
  // Upload is a Pioneer/Guardian capability. Not referrerAddress on its own: trust, trustee and
  // trust-admin invites set it too (to the inviter's wallet), so it doesn't mean "redeemed a
  // guardian invite code".
  const canMint = isGuardian || isPioneer;
  const link = emailVerified
    ? canMint
      ? AppRoutes.Upload.path
      : AppRoutes.Profile.subRoutes.Identification.path
    : AppRoutes.Profile.subRoutes.Identification.path;
  const text = emailVerified ? (canMint ? "Upload" : "Enter referral code") : "Complete email verification";

  return (
    <Container>
      {!tokensLoading && !tokens && (
        <EmptyBoxNew>
          {/* <span>There is nothing here yet!</span>
          <span>Try to upload your first land</span>
          <ScanButton onClick={() => navigator(link)}>{text}</ScanButton> */}


          <div style={{
            display: 'flex',
            justifyContent: 'start',
            borderBottom: '1px solid black',
            width: '100%',
            padding: '12px 16px'
          }}>
            Important
          </div>

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: '16px', md: '24px' },
            padding: { xs: '12px', md: '16px' },
            width: '100%'
          }}>
            <Box sx={{
              flex: { xs: '1', md: '1' },
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: '8px', md: '12px' }
            }}>
              Looks like you haven't uploaded any land yet.
            </Box>

            <Box sx={{
              flex: { xs: '1', md: '1' },
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: '8px', md: '12px' }
            }}>
              In order to upload a upload a land you will need a referral code from another Guardian account or Trustee account.
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                flexWrap: 'wrap'
              }}>
                <a href={link}>Here's</a> our in-depth story covering this process step by step.
              </div>
              <Box sx={{
                display: 'flex',
                justifyContent: { xs: 'center', md: 'flex-end' },
                width: '100%',
                marginTop: { xs: '8px', md: '16px' }
              }}>
                <ScanButton onClick={() => navigator(link)}>{text}</ScanButton>
              </Box>
            </Box>
          </Box>

        </EmptyBoxNew>
      )}

      {isLoading && (
        <EmptyBox>
          <span>🔎</span>
          <span>Data is loading...</span>
        </EmptyBox>
      )}

      <div style={{ display: isLoading || (!tokensLoading && !tokens) ? "none" : "block", width: "100%" }}>
        <TableComponent
          mobileTitle="My lands"
          setLoading={setLoading}
          batch={5}
          schema={isMobile ? responsiveSchema : colSchema}
          customData={getCachedData('profileTokens') || []}
          apiURL=""
          title={"My lands"}
          totalItems={tokens}
        />
      </div>

      {selectedId && (
        <TreeClaimModalNew
          token={selectedData}
          guardian={true}
          id={selectedId}
          setId={setSelectedId}
          name={selectedData.name}
          content={selectedData.content}
          targetDate={selectedData.targetDate}
        />
      )}
    </Container>
  );
};

const Container = ({ children }: any) => (
  <Profile>
    <Card sx={{ backgroundColor: "back.table", height: "100%" }}>{children}</Card>
    <Grid width="100%" flexDirection="column">
      <Trending sx={{ position: 'relative' }}>
        <img src={PalmTree} alt="Jungle" style={{ width: "100%", height: "auto" }} />
        <BlogStoryBlock
          blog={blogs[3]}
          sx={{
            position: 'absolute',
            top: 0,
            zIndex: 1,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'back.explorerGreen'
            },
            '& #blogTitle': {
              color: 'white',
            },
            '& .MuiAccordionSummary-content': {
              color: 'white',
            },
            '.css-l6rh7j': {
              borderBottom: '1px solid white'
            }
          }}
          search="blog-article-1"
        />
      </Trending >

    </Grid>
  </Profile>
);

// Every button in an Actions column takes one fixed height, so a cell that mixes text and
// icon-only buttons lines them up instead of letting each one's content set its size. The
// padding here is inner padding, horizontal only — the height is already fixed.
const TABLE_ACTION_HEIGHT = "36px";

const TableActionButton = styled(Button)({
  height: TABLE_ACTION_HEIGHT,
  minHeight: TABLE_ACTION_HEIGHT,
  padding: "0 16px",
  whiteSpace: "nowrap",
  backgroundColor: "black",
  color: "white",
  fontFamily: "'Inter', sans-serif",
  fontSize: 14,
  fontWeight: 400,
  border: "none",
  "&:hover": {
    backgroundColor: "#333",
    border: "none",
    color: "white",
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    color: "rgba(255, 255, 255, 0.6)",
  },
});

// Icon-only actions: same height, tighter inner padding so the button stays near-square.
// `minWidth: "unset"` overrides MUI's 64px Button minimum, which would stretch it to a slab.
const tableActionIconSx = { minWidth: "unset", padding: "0 8px" };

export const EmptyBox = styled("div")`
  background:#d1efff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  height: 330px;
  padding: 0 12px;
  user-select: none;

  span {
    margin-bottom: 15px;
    font-size: 26px;

    &:nth-of-type(1) {
      font-size: 26px;
    }
    &:nth-of-type(3) {
      font-size: 16px;
      color: black;
    }

    @media (max-width: 560px) {
      font-size: 14px;
      &:nth-of-type(3) {
        font-size: 14px;
      }
    }
  }
`;




export const Trending = styled("div")`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(69vw - 24px);
  max-height: 600px;
  overflow: hidden;

  & > img {
    position: absolute;
    display: block;
    width: auto;
    min-width: 100%;
    margin: 0;
    padding: 0;
    height: auto;
  }
`;

export const EmptyBoxNew = styled("div")`
  background:#d1efff;
  display: flex;
  align-items: start;
  justify-content: start;
  flex-direction: column;
  width: 100%;
  gap: 16px;
  height: 330px;
  padding: 24px;
  user-select: none;

  span {
    margin-bottom: 15px;
    font-size: 26px;

    &:nth-of-type(1) {
      font-size: 26px;
    }
    &:nth-of-type(3) {
      font-size: 16px;
      color: black;
    }

    @media (max-width: 560px) {
      font-size: 14px;
      &:nth-of-type(3) {
        font-size: 14px;
      }
    }
  }
`;

export const ScanButton = styled(Button)`
  text-transform: none;

`;

export default ProfileGuarding;
